/**
 * Nested division helpers (parent_division_id on divisions).
 */

export function getDivisionSubtreeIds(divisions, rootDivisionId) {
  const rootId = Number(rootDivisionId);
  if (!rootId) return [];

  const childrenByParent = new Map();
  for (const d of divisions) {
    const parentKey =
      d.parent_division_id == null || d.parent_division_id === ''
        ? 'root'
        : Number(d.parent_division_id);
    if (!childrenByParent.has(parentKey)) childrenByParent.set(parentKey, []);
    childrenByParent.get(parentKey).push(Number(d.id));
  }

  const ids = new Set([rootId]);
  const stack = [rootId];
  while (stack.length) {
    const cur = stack.pop();
    for (const childId of childrenByParent.get(cur) || []) {
      if (!ids.has(childId)) {
        ids.add(childId);
        stack.push(childId);
      }
    }
  }
  return Array.from(ids);
}

export function getAncestorDivisionIds(divisions, divisionId) {
  const byId = new Map(divisions.map((d) => [Number(d.id), d]));
  const ids = [];
  let cur = Number(divisionId);
  const seen = new Set();
  while (cur && !seen.has(cur)) {
    ids.push(cur);
    seen.add(cur);
    const row = byId.get(cur);
    if (!row || row.parent_division_id == null || row.parent_division_id === '') break;
    cur = Number(row.parent_division_id);
  }
  return ids;
}

/** True when division row is active (API may return 0/1 or boolean). */
export function divisionIsActive(d) {
  return d.is_active !== 0 && d.is_active !== '0' && d.is_active !== false;
}

/** Flat list sorted for dropdowns: root divisions first, children indented. */
export function flattenDivisionsTree(divisions, { includeInactive = false } = {}) {
  const active = includeInactive
    ? divisions
    : divisions.filter((d) => divisionIsActive(d));
  const childrenByParent = new Map();
  for (const d of active) {
    const parentKey =
      d.parent_division_id == null || d.parent_division_id === ''
        ? 'root'
        : Number(d.parent_division_id);
    if (!childrenByParent.has(parentKey)) childrenByParent.set(parentKey, []);
    childrenByParent.get(parentKey).push(d);
  }
  const sortByName = (arr) =>
    [...arr].sort((a, b) => String(a.name).localeCompare(String(b.name)));

  const flat = [];
  const walk = (parentKey, depth) => {
    for (const d of sortByName(childrenByParent.get(parentKey) || [])) {
      flat.push({ ...d, depth });
      walk(Number(d.id), depth + 1);
    }
  };
  walk('root', 0);

  // Inactive parents break the tree walk — still show those divisions in admin lists.
  if (includeInactive) {
    const seen = new Set(flat.map((d) => Number(d.id)));
    const byId = new Map(divisions.map((d) => [Number(d.id), d]));
    const depthOf = (d) => {
      let depth = 0;
      let cur = d;
      const guard = new Set();
      while (cur?.parent_division_id != null && cur.parent_division_id !== '') {
        const pid = Number(cur.parent_division_id);
        if (guard.has(pid)) break;
        guard.add(pid);
        depth += 1;
        cur = byId.get(pid);
        if (!cur) break;
      }
      return depth;
    };
    for (const d of sortByName(divisions)) {
      if (!seen.has(Number(d.id))) {
        flat.push({ ...d, depth: depthOf(d) });
      }
    }
  }

  return flat;
}

export function formatDivisionLabel(division, { showParent = false } = {}) {
  const indent = division.depth > 0 ? `${'— '.repeat(division.depth)}` : '';
  if (showParent && division.parent_name) {
    return `${indent}${division.name} (under ${division.parent_name})`;
  }
  return `${indent}${division.name}`;
}
